import { Bookmark, CheckCircle2, Clock, ArrowRight, BookmarkCheck } from "lucide-react";

export interface TipRecord {
  id: string;
  title: string;
  body?: string | null;
  category?: string | null;
  created_at?: string | null;
  image_url?: string | null;
  author?: {
    full_name?: string | null;
    profile_photo?: string | null;
    role?: string | null;
  } | null;
}

interface TipCardProps {
  tip: TipRecord;
  bn: boolean;
  isSaved?: boolean;
  onSave?: () => void;
  onRead?: () => void;
}

const TipCard = ({ tip, bn, isSaved = false, onSave, onRead }: TipCardProps) => {
  const authorName = tip.author?.full_name || (bn ? "বিশেষজ্ঞ" : "Health professional");
  const authorInitial = authorName.charAt(0).toUpperCase();
  const category = tip.category || (bn ? "স্বাস্থ্য" : "Health");
  const readTime = Math.max(1, Math.ceil((tip.body || "").trim().split(/\s+/).filter(Boolean).length / 180));
  const firstLine = (tip.body || "").split(/\r?\n/).find((line) => line.trim()) || "";

  return (
    <article className="overflow-hidden rounded-[20px] border border-[#e2ebe4] bg-white">
      <div className="flex min-h-[176px] items-stretch">
        <div className="flex min-w-0 flex-1 flex-col p-4 sm:p-5">
          <div className="flex items-center gap-2">
            {tip.author?.profile_photo ? (
              <img src={tip.author.profile_photo} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
            ) : (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e3f2ed] text-sm font-bold text-[#237e76]">
                {authorInitial}
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-xs font-bold text-[#20534f]">{authorName}</p>
              <p className="mt-0.5 flex items-center gap-1 text-[10px] text-[#71807d]">
                <CheckCircle2 className="h-3 w-3 text-[#168878]" />
                {bn ? "যাচাইকৃত বিশেষজ্ঞ" : "Verified professional"}
              </p>
            </div>
            <button
              type="button"
              onClick={onSave}
              aria-label={isSaved ? (bn ? "সংরক্ষিত টিপ সরান" : "Remove saved tip") : (bn ? "টিপ সংরক্ষণ করুন" : "Save tip")}
              className={`ml-auto shrink-0 rounded-full p-1.5 ${isSaved ? "bg-[#e4f3ef] text-[#168878]" : "text-[#237e76]"}`}
            >
              {isSaved ? <BookmarkCheck className="h-5 w-5" /> : <Bookmark className="h-5 w-5" />}
            </button>
          </div>

          <div className="mt-4 min-w-0 flex-1">
            <h2 className="line-clamp-2 font-serif text-[18px] font-extrabold leading-6 text-[#174e49]">{tip.title}</h2>
            {firstLine && <p className="mt-2 line-clamp-1 text-xs leading-5 text-[#647470]">{firstLine}</p>}
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-[10px] text-[#71807d]">
              <Clock className="h-3 w-3" />
              {readTime} {bn ? "মিনিট পড়া" : "min read"} · {category}
            </p>
            <button type="button" onClick={onRead} className="inline-flex items-center gap-1 rounded-full border border-[#d6e8df] px-3 py-1.5 text-[10px] font-bold text-[#237e76]">
              {bn ? "পড়ুন" : "Read"} <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </div>

        {tip.image_url && (
          <div className="relative w-[38%] shrink-0 bg-[#eff8f2]">
            <img src={tip.image_url} alt="" className="h-full w-full object-cover" />
          </div>
        )}
      </div>
    </article>
  );
};

export default TipCard;